import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Navbar.module.css';
import content from '../../data/content.json';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const { navbar } = content.global;

/**
 * Navbar Component - Fixed site navigation
 *
 * @component
 * @features
 * - Responsive mobile menu
 * - Auto-closes on route change
 * - Active link highlighting
 * - Scroll-based background opacity
 */
const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Auto-close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock page scroll while the mobile drawer is open, so scrolling the
  // menu can't move the content behind it (position-fixed lock — iOS
  // ignores plain overflow:hidden for touch scrolling)
  useBodyScrollLock(isMobileMenuOpen);

  // Track scroll for navbar background opacity
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Check if link is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav 
      className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className={styles.navbarContainer}>
        {/* Logo */}
        <Link 
          to="/" 
          className={styles.brand}
          aria-label={navbar.brand_aria_label}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/new logo.png`}
            alt={navbar.logo_alt}
            className={styles.logo}
            width="30"
            height="30"
          />
        </Link>

        {/* Mobile Menu Toggle */}
        <button
          className={styles.mobileToggle}
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-controls="navbar-menu"
          aria-label={navbar.mobile_toggle_aria_label}
        >
          <span className={styles.toggleIcon}>
            {isMobileMenuOpen ? '✕' : '☰'}
          </span>
        </button>

        {/* Navigation Links */}
        <div 
          id="navbar-menu"
          className={`${styles.navMenu} ${isMobileMenuOpen ? styles.open : ''}`}
        >
          <ul className={styles.navList}>
            <li className={styles.navItem}>
              <Link 
                to="/" 
                className={`${styles.navLink} ${isActive('/') ? styles.active : ''}`}
              >
                {navbar.links.home}
              </Link>
            </li>
            <li className={styles.navItem}>
              <Link
                to="/gallery"
                className={`${styles.navLink} ${isActive('/gallery') ? styles.active : ''}`}
              >
                {navbar.links.gallery}
              </Link>
            </li>
            <li className={styles.navItem}>
              <Link to="/story"
                className={`${styles.navLink} ${isActive('/story') ? styles.active : ''}`}
              >
                {navbar.links.story}
              </Link>
            </li>
            <li className={styles.navItem}>
              <Link
                to="/collaborations"
                className={`${styles.navLink} ${isActive('/collaborations') ? styles.active : ''}`}
              >
                {navbar.links.collaborate}
              </Link>
            </li>
            <li className={styles.navItem}>
              <Link
                to="/contact"
                className={`${styles.navLink} ${isActive('/contact') ? styles.active : ''}`}
              >
                {navbar.links.contact}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className={styles.overlay}
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

export default Navbar;